import UploadLock, { whenNoUpload, UPLOAD_LOCK_HINT, lockedStyle } from '../../upload/UploadLock';
import { useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Typography,
} from '@material-ui/core';
import {
    CheckCircleOutlined,
    CloudUploadOutlined,
    DescriptionOutlined,
    ErrorOutlineOutlined,
    WarningRounded,
} from '@material-ui/icons';

const SUCCESS_DISPLAY_MS = 800;
const FADE_OUT_MS = 200;

// Espelha o catálogo backend (`document-knowledge/*.skill.js > requiredFormat.extensions`).
// Atualizar aqui quando uma skill nova ganhar `requiredFormat`.
const REQUIRED_FORMATS_BY_KIND = {
    service_main_petition: ['doc', 'docx'],
    service_main_contract: ['doc', 'docx'],
    service_main_notion: ['doc', 'docx'],
    service_main_hearing_analysis: ['doc', 'docx'],
};

const getFileExtension = (name) => {
    if (!name || typeof name !== 'string') return '';
    const dot = name.lastIndexOf('.');
    if (dot < 0 || dot === name.length - 1) return '';
    return name.slice(dot + 1).toLowerCase();
};

const candidateBlockedByFormat = (documentKind, doc) => {
    const allowed = REQUIRED_FORMATS_BY_KIND[documentKind];
    if (!allowed) return null;
    const ext = getFileExtension(doc && doc.name);
    if (!ext) return null;
    if (allowed.includes(ext)) return null;
    return { actualExtension: ext, requiredExtensions: allowed };
};

const MissingRequiredDocumentsModal = ({
    open = false,
    pendencies = [],
    attachedDocuments = [],
    acceptedExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.png', '.jpg', '.jpeg', '.pjc'],
    onAttach,
    onSelectExisting,
    onClose,
    onItemResolved,
}) => {

    const attachInputRefs = useRef({});
    const [attachingKind, setAttachingKind] = useState(null);
    const [successKind, setSuccessKind] = useState(null);
    const [fadingOutKind, setFadingOutKind] = useState(null);
    const [draggingKind, setDraggingKind] = useState(null);
    const [selectingDocId, setSelectingDocId] = useState(null);
    const [errorByKind, setErrorByKind] = useState({});

    const items = Array.isArray(pendencies) ? pendencies : [];
    const candidates = Array.isArray(attachedDocuments) ? attachedDocuments : [];

    const triggerAttach = (documentKind) => {
        const ref = attachInputRefs.current[documentKind];
        if (!ref) return;
        ref.value = '';
        ref.click();
    };

    const setKindError = (documentKind, message) => {
        setErrorByKind(prev => ({ ...prev, [documentKind]: message }));
    };

    const clearKindError = (documentKind) => {
        setErrorByKind(prev => {
            if (!(documentKind in prev)) return prev;
            const next = { ...prev };
            delete next[documentKind];
            return next;
        });
    };

    const validateFileFormat = (documentKind, file) => {
        const required = REQUIRED_FORMATS_BY_KIND[documentKind];
        if (!required) return null;
        const ext = getFileExtension(file && file.name);
        if (!ext) return `Não consegui identificar o formato do arquivo. Envie em ${required.join(' ou ')}.`;
        if (required.includes(ext)) return null;
        return `Esse documento aceita apenas ${required.join(' ou ')}. Envie a versão editável (não PDF) para o cliente revisar.`;
    };

    const handleAttachFile = async (documentKind, file) => {
        if (!file || attachingKind) return;
        clearKindError(documentKind);
        const formatError = validateFileFormat(documentKind, file);
        if (formatError) {
            setKindError(documentKind, formatError);
            return;
        }
        setAttachingKind(documentKind);
        try {
            const result = typeof onAttach === 'function' ? await onAttach(documentKind, file) : null;
            if (result && result.success) {
                setAttachingKind(null);
                setSuccessKind(documentKind);
                await new Promise(r => setTimeout(r, SUCCESS_DISPLAY_MS));
                setFadingOutKind(documentKind);
                await new Promise(r => setTimeout(r, FADE_OUT_MS));
                setSuccessKind(null);
                setFadingOutKind(null);
                if (typeof onItemResolved === 'function') onItemResolved(documentKind);
                return;
            }
            const message = (result && result.message) || 'Não consegui anexar o documento. Tente novamente.';
            setKindError(documentKind, message);
        } catch (err) {
            setKindError(documentKind, 'Erro inesperado ao anexar. Tente novamente.');
        } finally {
            setAttachingKind(null);
        }
    };

    const handleInputChange = (documentKind, event) => {
        const file = (event && event.target && event.target.files && event.target.files[0]) || null;
        handleAttachFile(documentKind, file);
    };

    const handleDrop = (documentKind, event) => {
        event.preventDefault();
        event.stopPropagation();
        setDraggingKind(null);
        const file = (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]) || null;
        handleAttachFile(documentKind, file);
    };

    const handleSelectExisting = async (documentKind, documentId) => {
        if (selectingDocId || attachingKind) return;
        if (typeof onSelectExisting !== 'function') return;
        setSelectingDocId(documentId);
        try {
            const result = await onSelectExisting(documentKind, documentId);
            if (result && result.success) {
                setSelectingDocId(null);
                setSuccessKind(documentKind);
                await new Promise(r => setTimeout(r, SUCCESS_DISPLAY_MS));
                setFadingOutKind(documentKind);
                await new Promise(r => setTimeout(r, FADE_OUT_MS));
                setSuccessKind(null);
                setFadingOutKind(null);
                if (typeof onItemResolved === 'function') onItemResolved(documentKind);
                return;
            }
        } finally {
            setSelectingDocId(null);
        }
    };

    const renderCandidates = (item, anyBusy) => {
        if (candidates.length === 0) return null;
        if (typeof onSelectExisting !== 'function') return null;
        return (
            <Box style={{ marginTop: 16 }}>
                <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.55)', fontWeight: 500 }}>
                    Ou marque um documento já anexado como peça principal:
                </Typography>
                <Box style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {candidates.map((doc) => {
                        const isSelecting = selectingDocId === doc.id;
                        const formatBlocked = candidateBlockedByFormat(item.documentKind, doc);
                        const disabled = anyBusy || (selectingDocId !== null && !isSelecting) || !!formatBlocked;
                        const buttonLabel = formatBlocked
                            ? `Disponível só em ${formatBlocked.requiredExtensions.join('/')}`
                            : 'Marcar como peça principal';
                        return (
                            <Box
                                key={doc.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 12,
                                    padding: '12px 14px',
                                    border: formatBlocked ? '1px solid #f5c6c0' : '1px solid rgba(0,0,0,0.12)',
                                    borderRadius: 10,
                                    background: formatBlocked ? '#fdecea' : '#FFFFFF',
                                    opacity: disabled && !isSelecting && !formatBlocked ? 0.6 : 1,
                                }}
                            >
                                <Box display="flex" alignItems="center" style={{ gap: 10, minWidth: 0 }}>
                                    <DescriptionOutlined style={{ fontSize: 20, color: formatBlocked ? '#D70015' : '#376fd0', flexShrink: 0 }} />
                                    <Box style={{ minWidth: 0 }}>
                                        <Typography
                                            variant="body2"
                                            style={{
                                                color: '#1A1A1A',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {doc.name || `Documento ${doc.id}`}
                                        </Typography>
                                        {formatBlocked && (
                                            <Typography
                                                variant="caption"
                                                style={{ color: '#D70015', display: 'block', marginTop: 2 }}
                                            >
                                                Esse documento precisa estar em {formatBlocked.requiredExtensions.join(' ou ')} para o cliente revisar.
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={disabled}
                                    onClick={() => handleSelectExisting(item.documentKind, doc.id)}
                                    style={{
                                        textTransform: 'none',
                                        borderColor: formatBlocked ? 'rgba(0,0,0,0.18)' : '#376fd0',
                                        color: formatBlocked ? 'rgba(0,0,0,0.45)' : '#376fd0',
                                        fontSize: 13,
                                        fontWeight: 500,
                                        flexShrink: 0,
                                    }}
                                    title={formatBlocked ? `Aceito apenas: ${formatBlocked.requiredExtensions.join(', ')}` : undefined}
                                >
                                    {isSelecting ? (
                                        <CircularProgress size={14} thickness={5} style={{ color: '#376fd0' }} />
                                    ) : buttonLabel}
                                </Button>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        );
    };

    const renderDropzone = (item, anyBusy) => {
        const isDragging = draggingKind === item.documentKind;
        return (
            <Box
                role="button"
                tabIndex={0}
                onClick={() => !anyBusy && triggerAttach(item.documentKind)}
                onKeyDown={(e) => {
                    if (anyBusy) return;
                    if (e.key === 'Enter' || e.key === ' ') triggerAttach(item.documentKind);
                }}
                onDragOver={(e) => {
                    e.preventDefault();
                    if (anyBusy) return;
                    setDraggingKind(item.documentKind);
                }}
                onDragLeave={() => setDraggingKind(prev => (prev === item.documentKind ? null : prev))}
                onDrop={(e) => !anyBusy && handleDrop(item.documentKind, e)}
                style={{
                    marginTop: 24,
                    border: `2px dashed ${isDragging ? '#376fd0' : 'rgba(0,0,0,0.16)'}`,
                    borderRadius: 12,
                    background: isDragging ? '#F5F8FF' : '#FAFAFA',
                    padding: '28px 16px',
                    textAlign: 'center',
                    cursor: anyBusy ? 'not-allowed' : 'pointer',
                    transition: 'border-color 180ms ease, background 180ms ease',
                    outline: 'none',
                }}
            >
                <CloudUploadOutlined style={{ fontSize: 32, color: '#376fd0', marginBottom: 8 }} />
                <Typography variant="body2" style={{ fontWeight: 500, color: 'rgba(0,0,0,0.78)', lineHeight: 1.45 }}>
                    Solte o arquivo aqui ou clique pra selecionar
                </Typography>
                <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.5)', display: 'block', marginTop: 6 }}>
                    PDF, Word, Excel, imagens (JPG, PNG) e PJC
                </Typography>
                <input
                    ref={(el) => { attachInputRefs.current[item.documentKind] = el; }}
                    type="file"
                    accept={acceptedExtensions.join(',')}
                    onChange={(e) => handleInputChange(item.documentKind, e)}
                    style={{ display: 'none' }}
                />
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose && whenNoUpload(onClose)}
            maxWidth="md"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: 16,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                },
            }}
            transitionDuration={{ enter: 280, exit: 220 }}
        >
            <DialogTitle disableTypography style={{ padding: '32px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                <Box display="flex" alignItems="flex-start" style={{ gap: 16 }}>
                    <Box
                        style={{
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            background: '#FFF1F1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <WarningRounded style={{ color: '#D70015', fontSize: 24 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" style={{ fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3 }}>
                            {items.length === 1
                                ? 'Documento pendente para concluir'
                                : `${items.length} documentos pendentes para concluir`}
                        </Typography>
                        <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.65)', marginTop: 6, lineHeight: 1.5 }}>
                            Anexe {items.length === 1 ? 'o documento' : 'cada documento'} ou marque um arquivo já enviado.
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent style={{ padding: 0 }}>
                {items.map((item, idx) => {
                    const isAttaching = attachingKind === item.documentKind;
                    const isSuccess = successKind === item.documentKind;
                    const isFadingOut = fadingOutKind === item.documentKind;
                    const anyBusy = attachingKind !== null || successKind !== null || selectingDocId !== null;
                    return (
                        <Box
                            key={item.documentKind || `pendency-${idx}`}
                            style={{
                                opacity: isFadingOut ? 0 : 1,
                                transition: `opacity ${FADE_OUT_MS}ms ease`,
                            }}
                        >
                            <Box style={{ padding: '32px' }}>
                                <Typography
                                    variant="body1"
                                    style={{
                                        fontWeight: 500,
                                        fontSize: 16,
                                        color: '#1A1A1A',
                                    }}
                                >
                                    {item.displayLabel}
                                </Typography>

                                {isSuccess ? (
                                    <Box display="flex" alignItems="center" style={{ gap: 10, marginTop: 16 }}>
                                        <CheckCircleOutlined style={{ color: '#009700', fontSize: 18 }} />
                                        <Typography variant="body2" style={{ color: '#009700', fontWeight: 500, lineHeight: 1.5, fontSize: 14 }}>
                                            Documento aceito
                                        </Typography>
                                    </Box>
                                ) : isAttaching ? (
                                    <Box display="flex" alignItems="center" style={{ gap: 10, marginTop: 16 }}>
                                        <CircularProgress size={14} thickness={5} style={{ color: '#376fd0' }} />
                                        <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.65)', lineHeight: 1.5, fontSize: 14 }}>
                                            Analisando documento…
                                        </Typography>
                                    </Box>
                                ) : (
                                    item.reason && (
                                        <Typography
                                            variant="body2"
                                            style={{
                                                color: 'rgba(0,0,0,0.65)',
                                                marginTop: 12,
                                                lineHeight: 1.5,
                                                fontSize: 14,
                                            }}
                                        >
                                            {item.reason}
                                        </Typography>
                                    )
                                )}

                                {!isSuccess && !isAttaching && errorByKind[item.documentKind] && (
                                    <Box display="flex" alignItems="flex-start" style={{ gap: 8, marginTop: 12, padding: '10px 12px', background: '#FFF1F1', borderRadius: 8 }}>
                                        <ErrorOutlineOutlined style={{ color: '#D70015', fontSize: 18, flexShrink: 0, marginTop: 1 }} />
                                        <Typography variant="body2" style={{ color: '#A30010', lineHeight: 1.45, fontSize: 13 }}>
                                            {errorByKind[item.documentKind]}
                                        </Typography>
                                    </Box>
                                )}
                                {!isSuccess && !isAttaching && renderCandidates(item, anyBusy)}
                                {!isSuccess && !isAttaching && renderDropzone(item, anyBusy)}
                            </Box>
                            {idx < items.length - 1 && <Divider />}
                        </Box>
                    );
                })}

                {typeof onClose === 'function' && (
                    <Box display="flex" justifyContent="flex-end" style={{ padding: '16px 32px 24px' }}>
                        <UploadLock>{(inFlight) => (
                        <Button
                            onClick={onClose}
                            disabled={attachingKind !== null || selectingDocId !== null || inFlight} title={inFlight ? UPLOAD_LOCK_HINT : undefined}
                            style={{
                                textTransform: 'none',
                                color: 'rgba(0,0,0,0.55)',
                                fontSize: 13,
                                fontWeight: 500,
                                ...lockedStyle(inFlight),
                            }}
                        >
                            Fechar
                        </Button>
                        )}</UploadLock>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default MissingRequiredDocumentsModal;
